import { useState, useEffect, useRef, useMemo } from 'react';
import { gsap } from 'gsap';

const TextType = ({
  text = '',
  as: ElementType = 'div',
  typingSpeed = 50,
  initialDelay = 0,
  pauseDuration = 2000,
  deletingSpeed = 30,
  loop = true,
  className = '',
  showCursor = true,
  hideCursorWhileTyping = false,
  cursorCharacter = '|',
  cursorBlinkDuration = 0.5,
  cursorClassName = '',
  textColors = [],
  variableSpeed,
  onSentenceComplete,
  startOnVisible = false,
  reverseMode = false
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(!startOnVisible);
  
  const containerRef = useRef(null);
  const cursorRef = useRef(null);
  const texts = useMemo(() => (Array.isArray(text) ? text : [text]), [text]);

  useEffect(() => {
    if (startOnVisible && containerRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [startOnVisible]);

  useEffect(() => {
    if (showCursor && cursorRef.current) {
      gsap.to(cursorRef.current, {
        opacity: 0,
        duration: cursorBlinkDuration,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut"
      });
    }
  }, [showCursor, cursorBlinkDuration]);

  useEffect(() => {
    if (!isVisible || isPaused) return;

    const currentSentence = texts[currentIndex];
    let timeout;

    if (isDeleting) {
      if (displayedText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayedText(prev => prev.slice(0, -1));
        }, deletingSpeed);
      } else {
        setIsDeleting(false);
        setCurrentIndex((prev) => (prev + 1) % texts.length);
        if (!loop && currentIndex === texts.length - 1) {
          setIsPaused(true);
        }
      }
    } else {
      if (displayedText.length < currentSentence.length) {
        const nextChar = currentSentence[displayedText.length];
        const speed = variableSpeed 
          ? Math.random() * (variableSpeed.max - variableSpeed.min) + variableSpeed.min 
          : typingSpeed;

        timeout = setTimeout(() => {
          setDisplayedText(prev => prev + nextChar);
        }, displayedText.length === 0 ? initialDelay : speed);
      } else {
        if (onSentenceComplete) onSentenceComplete(currentSentence, currentIndex);
        
        if (loop || currentIndex < texts.length - 1) {
          timeout = setTimeout(() => {
            setIsDeleting(true);
          }, pauseDuration);
        }
      }
    }

    return () => clearTimeout(timeout);
  }, [
    displayedText, 
    isDeleting, 
    currentIndex, 
    isVisible, 
    isPaused, 
    texts, 
    typingSpeed, 
    deletingSpeed, 
    pauseDuration, 
    initialDelay, 
    loop, 
    variableSpeed, 
    onSentenceComplete
  ]);

  const currentColor = textColors[currentIndex % textColors.length] || 'inherit';
  const shouldHideCursor = hideCursorWhileTyping && !isDeleting && displayedText.length < texts[currentIndex].length;

  return (
    <ElementType ref={containerRef} className={className} style={{ color: currentColor }}>
      {displayedText}
      {showCursor && (
        <span 
          ref={cursorRef} 
          className={cursorClassName}
          style={{ visibility: shouldHideCursor ? 'hidden' : 'visible' }}
        >
          {cursorCharacter}
        </span>
      )}
    </ElementType>
  );
};

export default TextType;
